import { Loading } from "app/components/Wrappers";
import React, { useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { useSelector, useUrlSearch } from "app/utils/hooks";
import { RootState } from "typedefs";
import { schedulingFetchPost } from "app/utils/request/scheduling/fetch";
import { fetchPost } from "app/utils/request";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

/**
 * The message behind a refused checkout, or null when there is nothing to show.
 *
 * `/stripe_checkout` refuses a duplicate subscription with 422 and
 * `{ error: "You already have an active subscription for this offering." }`; the
 * request layer rejects with `{ error, errors, status }`, where `errors` is that
 * string wrapped in an array. Both shapes are read, and anything unrecognised
 * falls back to the caller's generic line.
 */
export const checkoutErrorMessage = (error: any): string | null => {
  if (typeof error?.error === "string" && error.error !== "") {
    return error.error;
  }
  if (Array.isArray(error?.errors)) {
    const message = error.errors.filter((e: any) => typeof e === "string").join(", ");
    return message === "" ? null : message;
  }
  return null;
};

const PostLoginHandler = () => {
  const [searchParams] = useUrlSearch();
  const { self } = useSelector((state: RootState) => state.user);
  const { t } = useTranslation();
  const history = useHistory();
  /**
   * CFTB-3445: this page used to log a failed checkout to the console and keep
   * spinning, so an athlete whose subscription was refused sat on a spinner with
   * no idea why. The reason the backend gave is shown instead.
   */
  const [error, setError] = useState<string | null>(null);

  const handleAddContact = (student: any) => {
    return fetchPost(`/contacts`, student);
  };

  const registerNewStudent = (student: any) => {
    const newContact = {
      contact: {
        first_name: student.firstName,
        last_name: student.lastName,
        email: student.email,
        coach_id: searchParams.coach_id,
        role: "athlete",
        sync_now: true,
      },
    };
    return handleAddContact(newContact);
  };

  const handleCheckout = async () => {
    try {
      if (searchParams.isSignIn) {
        await registerNewStudent(self);
      }

      schedulingFetchPost(
        `/stripe_checkout?coach_id=${searchParams.coachId}&student_id=${self.id}&stripe_product_id=${searchParams.productId}`,
      )
        .then((data) => {
          if (!data || !data.url) {
            throw new Error("Failed to checkout.");
          }
          window.location.href = data.url;
        })
        .catch((error) => {
          console.error("Error connecting to Stripe:", error);
          setError(
            checkoutErrorMessage(error) ||
              t("An unexpected error has occurred."),
          );
        });
    } catch (error) {
      console.error("Error registering student:", error);
      setError(t("An unexpected error has occurred."));
    }
  };

  /**
   * The signed-in user arrives in the store asynchronously, and this page is
   * mounted the moment the login redirect lands — so on the first render `self`
   * is often still null. Checking out then read `self.id` off it, threw, and was
   * swallowed by the `try` above: the athlete got "An unexpected error has
   * occurred." and no checkout, on a page whose whole job is to start one.
   *
   * So it waits for a user with an id, and the ref keeps it to one attempt —
   * `self` is a new object on every fetch of it, and a second `/stripe_checkout`
   * is a second subscription.
   */
  const started = useRef(false);

  useEffect(() => {
    if (!self?.id || started.current) {
      return;
    }
    started.current = true;
    handleCheckout();
  }, [self?.id]);

  if (error !== null) {
    return (
      <div className={styles.error_container}>
        <p className={styles.error_message}>{error}</p>
        <button
          type="button"
          className={`btn btn-primary ${styles.error_button}`}
          onClick={() => (history.length > 1 ? history.goBack() : history.push("/"))}
        >
          {t("Back")}
        </button>
      </div>
    );
  }

  return (
    <div className={styles.loading_container}>
      <Loading isLoading={true} loadType="spinner" />
    </div>
  );
};

export default PostLoginHandler;
